import {
  ChangeDetectionStrategy,
  Component,
  createEnvironmentInjector,
  EnvironmentInjector,
  inject,
  input,
  runInInjectionContext,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { take } from 'rxjs';

import { filterBoolean } from '@core/common-legacy';
import { ReportsAppKind } from '@core/reports-types';
import { tellentAppKindToReportsAppKind } from '@core/reports-util';
import { injectTellentAppKind, TellentWebComponent } from '@core/tellent-types';
import { defineCustomElements } from '@tellent/tellent-util';

import { ReportsInitDataService } from './reports-init.data.service';

@Component({
  selector: 'x-reports-shell',
  template: `<ng-content></ng-content>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TellentReportsShellComponent {
  private readonly envInjector = inject(EnvironmentInjector);
  private readonly tellentAppKind = injectTellentAppKind();
  private readonly initData = inject(ReportsInitDataService);

  public companyId = input<string>();

  constructor() {
    toObservable(this.companyId)
      .pipe(filterBoolean, take(1))
      .subscribe(companyId => this.initReports(companyId));
  }

  private async initReports(companyId: string) {
    const module = await import('./reports.provider');
    const { reportsComponents, provideTellentReports, initReportsData } = module;

    if (this.initData.dataInitialized) {
      return;
    }

    const appKind = this.getReportsAppKind();
    const providers = provideTellentReports(appKind, companyId);
    const injector = createEnvironmentInjector(providers, this.envInjector);
    defineCustomElements(reportsComponents, injector);
    runInInjectionContext(injector, () => initReportsData());
  }

  private getReportsAppKind(): ReportsAppKind {
    const appKind = tellentAppKindToReportsAppKind(this.tellentAppKind);
    if (!appKind) {
      throw new Error('Reports initialized for unsupported app kind');
    }
    return appKind;
  }
}

export type TellentReportsShellWebComponent = TellentWebComponent<
  TellentReportsShellComponent,
  'companyId'
>;
